import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Check, Loader2, Pencil, X, Send } from 'lucide-react';
import { formatChatTimestamp } from '../../utils/dateUtils';
import { TypeDropdown } from '../common/TypeDropdown';
import { RowCountDropdown } from '../common/RowCountDropdown';
import { CreditPill } from '../ui';
import { calculateLeadCost } from '../../constants/credits';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

const DEFAULT_ROW_COUNT_OPTIONS = [10, 25, 50, 100];

interface ChatMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  isLoading?: boolean;
  timestamp?: Date;
  criteria?: string[]; // For displaying criteria pills in confirmation messages
}

interface DatasetConfirmationViewProps {
  previewData: {
    search: {
      entity: { type: string };
      criteria: Array<{ description: string }>;
    };
    enrichments: Array<{
      description: string;
      format: string;
      options?: Array<{ label: string }>;
    }>;
  };
  searchQuery: string;
  rowCount: number;
  tableType: 'companies' | 'people' | 'custom';
  customType: string;
  isLoadingPreview: boolean;
  onConfirm: (sessionId: string | null) => void;
  onBack: () => void;
  onSearchQueryChange: (query: string) => void;
  onTableTypeChange: (type: 'companies' | 'people' | 'custom') => void;
  onCustomTypeChange: (customType: string) => void;
  onRowCountChange: (count: number) => void;
  chatMessages?: ChatMessage[];
  onChatMessagesChange?: (messages: ChatMessage[]) => void;
  generationError?: string | null;
}

type FlowStep = 'loading' | 'awaiting_confirmation' | 'running';

export const DatasetConfirmationView: React.FC<DatasetConfirmationViewProps> = ({
  previewData,
  searchQuery,
  rowCount,
  tableType,
  customType,
  isLoadingPreview,
  onConfirm,
  onBack,
  onSearchQueryChange,
  onTableTypeChange,
  onCustomTypeChange,
  onRowCountChange,
  chatMessages: externalMessages,
  onChatMessagesChange,
  generationError,
}) => {
  const [step, setStep] = useState<FlowStep>('loading');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [localCriteria, setLocalCriteria] = useState<string[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [sessionId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [pendingQueryRefresh, setPendingQueryRefresh] = useState(false);
  const [customRowCount, setCustomRowCount] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showRowCountDropdown, setShowRowCountDropdown] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const hasInitialized = useRef(false);

  const [displayedTitle, setDisplayedTitle] = useState('');
  const [titleComplete, setTitleComplete] = useState(false);

  useEffect(() => {
    const targetTitle = searchQuery || 'New Research';
    setDisplayedTitle('');
    setTitleComplete(false);
    let index = 0;

    const timer = setInterval(() => {
      if (index < targetTitle.length) {
        setDisplayedTitle(targetTitle.slice(0, index + 1));
        index++;
      } else {
        setTitleComplete(true);
        clearInterval(timer);
      }
    }, 35);

    return () => clearInterval(timer);
  }, [searchQuery]);

  const effectiveRowCount = customRowCount ? parseInt(customRowCount) : rowCount;

  useEffect(() => {
    if (previewData?.search?.criteria) {
      setLocalCriteria(previewData.search.criteria.map(c => c.description));
    }
  }, [previewData]);

  useEffect(() => {
    if (generationError) {
      setMessages(prev => prev.filter(m => !m.isLoading));
      setStep('awaiting_confirmation');
    }
  }, [generationError]);

  // When preview finishes loading after a query refresh, add the assistant message with updated criteria
  useEffect(() => {
    if (pendingQueryRefresh && !isLoadingPreview) {
      const criteria = previewData?.search?.criteria?.map(c => c.description) || [];

      setMessages(prev => {
        const withoutLoading = prev.filter(m => !m.isLoading);
        return [...withoutLoading, {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: `Got it! Here's what we'll run the search on:`,
          timestamp: new Date(),
          criteria: criteria
        }];
      });

      setPendingQueryRefresh(false);
      setIsSending(false);
    }
  }, [pendingQueryRefresh, isLoadingPreview, previewData]);

  const sendInitialMessage = useCallback(() => {
    if (hasInitialized.current || !searchQuery) return;
    hasInitialized.current = true;

    // Get criteria from previewData
    const criteria = previewData?.search?.criteria?.map(c => c.description) || [];

    // Show user query and static "Got it!" response immediately (no API call needed)
    // Include criteria in the assistant message for display as pills
    setMessages([
      {
        id: 'user-0',
        role: 'user',
        content: searchQuery,
        timestamp: new Date()
      },
      {
        id: 'assistant-1',
        role: 'assistant',
        content: `Got it! Here's what we'll run the search on:`,
        timestamp: new Date(),
        criteria: criteria // Pass criteria for pill display
      }
    ]);

    setStep('awaiting_confirmation');
  }, [searchQuery, previewData]);

  useEffect(() => {
    sendInitialMessage();
  }, [sendInitialMessage]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, step]);

  // Sync messages to external state for persistence
  useEffect(() => {
    if (onChatMessagesChange && messages.length > 0) {
      onChatMessagesChange(messages);
    }
  }, [messages, onChatMessagesChange]);

  // Initialize from external messages if provided
  useEffect(() => {
    if (externalMessages && externalMessages.length > 0 && messages.length === 0) {
      const convertedMessages: ChatMessage[] = externalMessages.map(msg => ({
        ...msg,
        timestamp: msg.timestamp || new Date()
      }));
      setMessages(convertedMessages);
      setStep('awaiting_confirmation');
      hasInitialized.current = true;
    }
  }, [externalMessages]);

  useEffect(() => {
    if (editingIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingIndex]);

  const handleConfirm = () => {
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content: 'Yes, run the search',
      timestamp: new Date()
    }]);

    setTimeout(() => {
      setStep('running');
      setMessages(prev => [...prev, {
        id: `running-${Date.now()}`,
        role: 'assistant',
        content: 'Running search...',
        isLoading: true,
        timestamp: new Date()
      }]);

      setTimeout(() => {
        onConfirm(sessionId);
      }, 300);
    }, 200);
  };

  const handleSendMessage = async () => {
    const text = inputValue.trim();
    if (!text || isSending) return;

    const userMessageId = `user-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: userMessageId,
      role: 'user',
      content: text,
      timestamp: new Date()
    }]);
    setInputValue('');

    const confirmPhrases = ['yes', 'yep', 'yeah', 'sure', 'ok', 'okay', 'looks good', 'lgtm', 'correct', 'right', 'go', 'run', 'do it', 'confirm', 'proceed', 'run it', 'run search'];
    const isConfirmation = confirmPhrases.some(phrase => text.toLowerCase().includes(phrase));

    if (isConfirmation && step === 'awaiting_confirmation') {
      setTimeout(() => {
        setStep('running');
        setMessages(prev => [...prev, {
          id: `running-${Date.now()}`,
          role: 'assistant',
          content: 'Running search...',
          isLoading: true,
          timestamp: new Date()
        }]);

        setTimeout(() => {
          onConfirm(sessionId);
        }, 300);
      }, 200);
      return;
    }

    // User is modifying their query - re-run the preview with the new query
    setIsSending(true);
    setMessages(prev => [...prev, {
      id: `loading-${Date.now()}`,
      role: 'assistant',
      content: '',
      isLoading: true,
      timestamp: new Date()
    }]);

    // Set flag to indicate we're waiting for preview refresh
    setPendingQueryRefresh(true);

    // Call the parent's handler to re-run the preview with the new query
    onSearchQueryChange(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEditCriterion = (index: number) => {
    setEditingIndex(index);
    setEditValue(localCriteria[index]);
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null && editValue.trim()) {
      const updated = [...localCriteria];
      updated[editingIndex] = editValue.trim();
      setLocalCriteria(updated);
    }
    setEditingIndex(null);
    setEditValue('');
  };

  const handleRemoveCriterion = (index: number) => {
    setLocalCriteria(prev => prev.filter((_, i) => i !== index));
  };

  const renderMessage = (message: ChatMessage) => {
    if (message.role === 'user') {
      return (
        <div
          key={message.id}
          className="flex justify-end animate-chatMessageUser group"
        >
          <div className="flex items-center gap-2">
            {message.timestamp && (
              <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                {formatChatTimestamp(message.timestamp)}
              </span>
            )}
            <div className="max-w-[80%] bg-gray-900 text-white rounded-2xl rounded-br-md px-4 py-2.5">
              <p className="text-[14px] leading-relaxed">{message.content}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={message.id}
        className="flex gap-3 animate-chatMessage group"
      >
        <div className="flex-shrink-0 w-7 h-7 rounded-full overflow-hidden mt-0.5">
          <img src={skylarqLogo} alt="Skylarq" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-semibold text-gray-400 tracking-wider uppercase">Skylarq</span>
            {message.timestamp && (
              <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                {formatChatTimestamp(message.timestamp)}
              </span>
            )}
          </div>
          {message.isLoading ? (
            <div className="text-gray-500">
              {message.content ? (
                <div className="flex items-center gap-2.5 text-[14px] text-gray-600">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span>{message.content}</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 bg-gray-100 rounded-full px-3 py-2">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-typingDot" style={{ animationDelay: '300ms' }} />
                </div>
              )}
            </div>
          ) : (
            <p className="text-[15px] text-gray-700 leading-relaxed">{message.content}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col bg-white overflow-hidden">
      <div className="flex-shrink-0">
        <div className="h-14 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              disabled={step === 'running'}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-[16px] font-semibold text-gray-900 truncate max-w-md">
              {displayedTitle}
              {!titleComplete && <span className="animate-pulse">|</span>}
            </h1>
          </div>
        </div>
        <div className="border-b border-gray-200" />
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-8 space-y-5">
          {messages.map(renderMessage)}

          {step !== 'loading' && messages.length >= 1 && !pendingQueryRefresh && (
            <div className="ml-11 space-y-3" data-tour="criteria-breakdown">
              <div className="space-y-2">
                {step === 'awaiting_confirmation' && localCriteria.length > 0 && (
                  <p className="text-[11px] text-gray-400 mb-1">Click any criterion to edit</p>
                )}
                {localCriteria.map((criterion, index) => (
                  <div
                    key={index}
                    onClick={() => step === 'awaiting_confirmation' && editingIndex !== index && handleEditCriterion(index)}
                    className={`group flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100 ${
                      step === 'awaiting_confirmation' && editingIndex !== index
                        ? 'cursor-pointer hover:border-gray-300 hover:bg-gray-100/50 transition-all'
                        : ''
                    }`}
                  >
                    {editingIndex === index ? (
                      <div className="flex-1 flex items-center gap-2">
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') {
                              setEditingIndex(null);
                              setEditValue('');
                            }
                          }}
                          onBlur={handleSaveEdit}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 px-3 py-1.5 text-[14px] bg-white border border-gray-300 rounded-md focus:outline-none focus:border-gray-400"
                        />
                      </div>
                    ) : (
                      <>
                        <span className="text-[13px] text-gray-400 font-mono mt-0.5">{index + 1}</span>
                        <p className="flex-1 text-[14px] text-gray-700">{criterion}</p>
                        {step === 'awaiting_confirmation' && (
                          <div className="flex items-center gap-1">
                            <Pencil className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveCriterion(index);
                              }}
                              className="p-1 text-gray-300 hover:text-red-500 rounded transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}

                {localCriteria.length === 0 && (
                  <p className="text-[14px] text-gray-400 italic py-2">No criteria defined.</p>
                )}
              </div>

              <div className="flex items-center gap-3 pt-2">
                <TypeDropdown
                  tableType={tableType}
                  customType={customType}
                  showDropdown={showTypeDropdown}
                  disabled={step !== 'awaiting_confirmation'}
                  onToggleDropdown={() => setShowTypeDropdown(!showTypeDropdown)}
                  onCloseDropdown={() => setShowTypeDropdown(false)}
                  onTypeChange={onTableTypeChange}
                  onCustomTypeChange={onCustomTypeChange}
                />
                <RowCountDropdown
                  rowCount={rowCount}
                  customRowCount={customRowCount}
                  effectiveRowCount={effectiveRowCount}
                  showDropdown={showRowCountDropdown}
                  options={DEFAULT_ROW_COUNT_OPTIONS}
                  disabled={step !== 'awaiting_confirmation'}
                  onToggleDropdown={() => setShowRowCountDropdown(!showRowCountDropdown)}
                  onCloseDropdown={() => setShowRowCountDropdown(false)}
                  onSelect={(count) => {
                    onRowCountChange(count);
                    setCustomRowCount('');
                  }}
                  onCustomChange={(val) => {
                    setCustomRowCount(val);
                    if (val) {
                      onRowCountChange(parseInt(val) || 10);
                    }
                  }}
                />
              </div>

              {step === 'awaiting_confirmation' && (
                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={handleConfirm}
                    disabled={localCriteria.length === 0}
                    className="flex items-center gap-2 px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Yes, run search
                    <CreditPill cost={calculateLeadCost(effectiveRowCount)} />
                  </button>
                  <button
                    onClick={() => {
                      setInputValue(searchQuery);
                      inputRef.current?.focus();
                    }}
                    className="px-4 py-2 text-[13px] text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full border border-gray-200 transition-colors"
                  >
                    Edit query
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex-shrink-0 px-6 pb-6 pt-2 bg-white">
        <div className="max-w-2xl mx-auto">
          {step === 'awaiting_confirmation' ? (
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message or edit your query..."
                disabled={isSending}
                className="w-full px-4 py-3 pr-12 text-[14px] bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:border-gray-300 focus:bg-white focus:shadow-sm transition-all disabled:opacity-50"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isSending}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 transition-colors"
              >
                {isSending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          ) : step === 'running' ? (
            <div className="flex items-center justify-center py-3 text-[13px] text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Generating your dataset...
            </div>
          ) : (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
